/*
 * userProfiling Sagas
 *
 * @version 1.0.0
 * @author NEOSYN\user
 * @since 18-Oct-2024
 */

import { put, take, call, all, select, takeLatest, delay } from 'redux-saga/effects'
import moment from 'moment'
import * as userProfilingActions from 'Store/actions/userProfiling'
import * as shopPickerActions from 'Store/actions/shopPicker'
import * as userProfilingApi from 'Api/userProfiling/userProfiling'
import * as homePageApi from 'Api/homepage/homepage'
import { doShowPopup } from 'Store/actions/popup'
import { getIntl } from 'Utility/intlIstance'
// import * as documentsApi from 'Api/documents/documents'
import { nullSafe, utmConverter } from 'Utility/globalMethods'
import {
  normalizeShopList,
  normalizeDisputeShopList,
  normalizeCostsShopList,
  normalizeShopSelectionPayload,
  normalizeCostsShopSelectionPayload,
  normalizeDisputeShopSelectionPayload,
  getActionsForPath,
  mapGtShop
} from 'Utility/shopListNormalizer'
import { doSetRefreshCostsContext } from '../../routes/Costs/modules/CostsActions'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { Capacitor } from '@capacitor/core'

const getPathname = (state) => nullSafe(() => state.router.location.pathname, '')
const getTabSelected = (state) => nullSafe(() => state.global.selectedTab, '')

function * getTabBarSaga (payload) {
  yield put(userProfilingActions.getTabBar.request(payload))
  const { error, response } = yield call(userProfilingApi.getTabBar, { ...payload })

  if (!error) {
    yield put(userProfilingActions.getTabBar.success(response))
  } else {
    yield put(userProfilingActions.getTabBar.failure(error, response))
  }
}

function * newMessagesSaga () {
  yield put(userProfilingActions.getNewMessages.request({}, { loading: true, persistAfterShopChange: true }))
  const { error, response } = yield call(userProfilingApi.getNewMessages, {})

  if (!error) {
    yield put(userProfilingActions.getNewMessages.success(response.data))
  } else {
    yield put(userProfilingActions.getNewMessages.failure(error, { hideErrorPopup: true, ...response }))
  }
}

function * newDocumentsSaga (payload) {
  yield put(
    userProfilingActions.getNewDocuments.request({ ...payload }, { loading: true, persistAfterShopChange: true })
  )
  const { error, response } = yield call(userProfilingApi.getNewDocuments, { ...payload })

  if (!error) {
    yield put(userProfilingActions.getNewDocuments.success(response.data))
  } else {
    yield put(userProfilingActions.getNewDocuments.failure(error, { hideErrorPopup: true, ...response }))
  }
}

function * getCostsShopList (payload) {
  const intl = getIntl()

  yield put(shopPickerActions.getCostsShopList.request({ ...payload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.getCostsShopList, { ...payload })

  if (!error) {
    const normalizedData = normalizeCostsShopList(response.data.selectorGroups, intl)
    yield put(shopPickerActions.getCostsShopList.success({ selectorGroups: normalizedData }))

    // Controlla il flag Redux invece del parametro API
    const state = yield select()
    const forceShowPopup = state.shopPicker.forceShowCostsShopPickerPopup

    if (forceShowPopup) {
      // User explicitly requested shop picker - always show popup
      yield put(doShowPopup('COSTS_SHOP_PICKER'))
      // Reset the flag after showing the popup
      yield put(shopPickerActions.doResetForceShowCostsShopPickerPopup())
    }
  } else {
    yield put(shopPickerActions.getCostsShopList.failure(error, { ...response }))
  }
}

function * getShopList (payload) {
  const intl = getIntl()
  const state = yield select()
  const isDisputeContext = state.shopPicker.shopSelectionFromPage === 'dispute-uci'
  const isFilterSearch = payload && (payload.filter !== undefined)

  if (isDisputeContext) {
    // In dispute context: call dispute endpoint, update selectorDisputeGroups
    yield put(shopPickerActions.getDisputeShopList.request({ ...payload }, { loading: true }))
    const { error, response } = yield call(userProfilingApi.getDisputeShopList, { ...payload })

    if (!error) {
      const normalizedData = normalizeDisputeShopList(response.data.selectorGroups, intl)
      yield put(shopPickerActions.getDisputeShopList.success({ selectorGroups: normalizedData }))
      // Only open popup on initial load, not on filter searches
      if (!isFilterSearch) {
        yield put(doShowPopup('DISPUTE_SHOP_PICKER'))
      }
    } else {
      yield put(shopPickerActions.getDisputeShopList.failure(error, { ...response }))
    }
    return
  }

  yield put(shopPickerActions.getShopList.request({ ...payload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.getShopList, { ...payload })

  if (!error) {
    const normalizedData = normalizeShopList(response.data.selectorGroups, intl)
    yield put(shopPickerActions.getShopList.success({ selectorGroups: normalizedData }))
    // Only open popup on initial load, not on filter searches
    if (!isFilterSearch) {
      yield put(doShowPopup('SHOP_PICKER'))
    }
  } else {
    yield put(shopPickerActions.getShopList.failure(error, { ...response }))
  }
}

function * getListOfShopsGt (payload) {
  yield put(shopPickerActions.getShopListGt.request({ ...payload }, { loading: true }))

  const { error, response } = yield call(userProfilingApi.getShopListGt, { ...payload })

  if (!error) {
    const intl = getIntl()
    const rawShopList = response?.data?.shopList ?? response?.data ?? []

    const normalized = rawShopList.map((shop) => mapGtShop(shop, intl))

    // Include pagination info along with the normalized shop list
    const responseWithPagination = {
      shops: normalized,
      total: response?.data?.total || 0,
      pageNumber: response?.data?.pageNumber || 0,
      pageSize: response?.data?.pageSize || 20
    }

    yield put(shopPickerActions.getShopListGt.success(responseWithPagination))
  } else {
    yield put(shopPickerActions.getShopListGt.failure(error, { hideErrorPopup: true, ...response }))
  }
}

export function * setShopSelection (group, data, selectorGroups) {
  const payload = normalizeShopSelectionPayload({ group, data, selectorGroups })

  if (payload.shopIds.length === 0) {
    yield put(shopPickerActions.doResetShopSelectionChanged())
    return
  }

  yield put(shopPickerActions.setShopSelection.request({ ...payload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.setShopSelection, { ...payload })

  if (!error) {
    yield put(shopPickerActions.setShopSelection.success(response.data))
    yield put(shopPickerActions.clearSearchTerm())

    const pathname = yield select(getPathname)
    const tabSelected = yield select(getTabSelected)

    // Usa window.location.pathname se diverso da Redux (fix per disallineamento router)
    const actualPathname = window.location.pathname !== pathname
      ? window.location.pathname
      : pathname

    const actionsToDispatch = getActionsForPath(actualPathname, tabSelected)

    // Delay per permettere al backend di aggiornare il contesto
    yield delay(600)

    // ripristino eventuali payload salvati
    const persisted = yield select((state) => state.persistedActions?.persisted || {})

    for (const creator of actionsToDispatch) {
      try {
        // Crea un'action temporanea per ottenere il tipo
        const tempAction = creator()
        const savedPayload = persisted[tempAction.type]

        // Se c'è un payload salvato, usa quello, altrimenti chiama senza parametri
        if (savedPayload !== undefined) {
          yield put(creator(savedPayload))
        } else {
          yield put(tempAction)
        }
      } catch (actionError) {
        console.error('Error dispatching action after shop selection:', actionError)
        // Continua con le altre action anche se una fallisce
      }
    }
  } else {
    yield put(shopPickerActions.setShopSelection.failure(error, { ...response }))
    yield put(shopPickerActions.clearSearchTerm())
  }
  yield put(shopPickerActions.doResetShopSelectionChanged())
}

function * getDisputeShopList (payload) {
  const intl = getIntl()
  const { autoCheck, forcePopup, ...apiPayload } = payload || {}
  yield put(shopPickerActions.getDisputeShopList.request({ ...apiPayload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.getDisputeShopList, { ...apiPayload })

  if (!error) {
    const normalizedData = normalizeDisputeShopList(response.data.selectorGroups, intl)
    yield put(shopPickerActions.getDisputeShopList.success({ selectorGroups: normalizedData }))

    // Conta i flat shop su tutti i gruppi (normalizeDisputeShopList produce shops: [] per ogni entry)
    const totalShops = normalizedData.reduce((acc, group) => acc + (group.shopsList || []).length, 0)

    if (!forcePopup && totalShops === 1) {
      // Mono-shop: auto-selezione senza mostrare il popup
      const onlyGroup = normalizedData[0]
      const onlyShop = onlyGroup.shopsList[0]
      const autoPayload = {
        group: onlyGroup.group,
        data: [{ parent: { ...onlyShop, isSelected: true }, shops: [] }],
        selectorGroups: normalizedData
      }
      yield call(setDisputeShopSelection, autoPayload)
    } else if (!autoCheck || forcePopup) {
      // Multi-shop oppure click esplicito: apri popup
      yield put(doShowPopup('DISPUTE_SHOP_PICKER'))
    }
    // Se autoCheck + multi-shop: non fare nulla — viene mostrato l'empty state
  } else {
    yield put(shopPickerActions.getDisputeShopList.failure(error, { ...response }))
  }
}

function * setDisputeShopSelection (payload) {
  const normalizedPayload = normalizeDisputeShopSelectionPayload(payload)
  yield put(shopPickerActions.setDisputeShopSelection.request({ ...normalizedPayload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.setDisputeShopSelection, { ...normalizedPayload })

  if (!error) {
    yield put(shopPickerActions.setDisputeShopSelection.success(response.data))
    yield put(shopPickerActions.clearSearchTerm())
  } else {
    yield put(shopPickerActions.setDisputeShopSelection.failure(error, { ...response }))
  }
}

function * setCostsShopSelection (payload) {
  // Transform the payload using the normalizer for costs context
  const normalizedPayload = normalizeCostsShopSelectionPayload(payload)
  const { shopIds, location = '' } = normalizedPayload
  const transformedPayload = { shopIds, location }

  const activeTab = yield select(getTabSelected)

  yield put(shopPickerActions.setCostsShopSelection.request({ ...transformedPayload }, { loading: true }))
  const { error, response } = yield call(userProfilingApi.setCostsShopSelection, { ...transformedPayload })

  if (!error) {
    yield put(shopPickerActions.setCostsShopSelection.success(response.data, { group: payload.group, data: payload.data }))
    yield put(shopPickerActions.clearSearchTerm())

    // Set flag to refresh costs context with current active tab
    yield put(doSetRefreshCostsContext({
      shouldRefresh: true,
      activeTab
    }))
  } else {
    yield put(shopPickerActions.setCostsShopSelection.failure(error, { ...response }))
  }
}

function * profilingSaga (payload) {
  yield put(userProfilingActions.profiling.request(payload))
  const { error, response } = yield call(userProfilingApi.profiling, { ...payload })
  if (!error) {
    const merchantId = nullSafe(() => response.data.merchant.id, '')
    const userMerchantId = nullSafe(() => response.data.user.merchantId, '')

    let hashedMerchantId = ''
    if (userMerchantId) {
      try {
        hashedMerchantId = yield call(utmConverter, userMerchantId)
      } catch (error) {
        console.error('Error hashing merchantId:', error)
      }
    }

    if (Capacitor.isNativePlatform() && merchantId !== '') {
      FirebaseAnalytics.setUserProperty({
        key: 'userID',
        value: merchantId
      })
    }

    // Add hashedMerchantId to the response
    const responseWithHashedId = {
      ...response,
      data: {
        ...response.data,
        hashedMerchantId
      }
    }

    const BANKS_HAVE_AML_FEATURE = ['NEXIBUSINESS']
    if (BANKS_HAVE_AML_FEATURE.includes(process.env.BANK)) yield call(getStatusAmlSaga)
    yield put(userProfilingActions.profiling.success(responseWithHashedId))
    // Call new messages and new documents on refresh page based on /profiling
    const pathname = yield select(getPathname)
    if (pathname && pathname !== '/documents') {
      yield put(userProfilingActions.doGetNewDocuments({ sourceFilter: 'HOME' }))
    }
    yield put(userProfilingActions.doGetNewMessages())
    yield put(userProfilingActions.doGetNavigationMenu())
    yield put(userProfilingActions.doGetNavigationProfile())
    if (nullSafe(() => response.data.authorizations.LINKS, false)) {
      yield put(userProfilingActions.doGetServiceQuickLinks())
    }
  } else {
    yield put(userProfilingActions.profiling.failure(error, response))
  }
}

function * getNavigationProfileSaga () {
  yield put(userProfilingActions.getNavigationProfile.request({}, { loading: true }))
  const { error, response } = yield call(homePageApi.getNavigationProfile, {})

  if (!error) {
    yield put(userProfilingActions.getNavigationProfile.success(response.data))
  } else {
    yield put(
      userProfilingActions.getNavigationProfile.failure(error, {
        hideErrorPopup: true,
        ...response
      })
    )
  }
}
function * getNavigationMenuSaga () {
  yield put(userProfilingActions.getNavigationMenu.request({}, { loading: true }))
  const { error, response } = yield call(homePageApi.getNavigationMenu, {})

  if (!error) {
    yield put(userProfilingActions.getNavigationMenu.success(response.data))
  } else {
    yield put(userProfilingActions.getNavigationMenu.failure(error, response))
  }
}
function * getStatusAmlSaga () {
  yield put(userProfilingActions.getAmlStatus.request({}, { loading: true }))
  const { error, response } = yield call(userProfilingApi.getStatusAml, {})

  if (!error) {
    yield put(userProfilingActions.getAmlStatus.success(response.data))
  } else {
    yield put(userProfilingActions.getAmlStatus.failure(error, {
      hideErrorPopup: true,
      ...response
    }))
  }
}

function * getResultsGraphViewedSaga () {
  yield put(userProfilingActions.getResultsGraphViewed.request({}, { loading: true }))
  // compute last 30 days period
  const endDate = moment().format('YYYY-MM-DD')
  const startDate = moment().subtract(29, 'days').format('YYYY-MM-DD')
  const params = {
    periodOneStartDate: startDate,
    periodOneEndDate: endDate
  }
  const { error, response } = yield call(userProfilingApi.getResultsGraphViewed, params)

  if (!error) {
    yield put(userProfilingActions.getResultsGraphViewed.success(response.data))
  } else {
    yield put(userProfilingActions.getResultsGraphViewed.failure(error, response))
  }
}

function * updateTutorialSaga (payload) {
  yield put(userProfilingActions.updateTutorial.request(payload, { loading: true }))
  const { error, response } = yield call(userProfilingApi.updateTutorial, payload)

  if (!error) {
    yield put(userProfilingActions.updateTutorial.success(response.data))
  } else {
    yield put(userProfilingActions.updateTutorial.failure(error, response))
  }
}

function * getServiceQuickLinksSaga (payload) {
  yield put(userProfilingActions.getServiceQuickLinks.request({ ...payload }, { loading: true, persistAfterShopChange: true }))
  const { error, response } = yield call(userProfilingApi.getServiceQuickLinks, {
    ...payload
  })

  if (!error) {
    yield put(userProfilingActions.getServiceQuickLinks.success(response.data))
  } else {
    yield put(
      userProfilingActions.getServiceQuickLinks.failure(error, {
        hideErrorPopup: true,
        ...response
      })
    )
  }
}
// @generator saga

// @generator saga:watch

function * watchGetStatusAml () {
  while (yield take(userProfilingActions.actionTypes.DO_GET_AML_STATUS)) {
    yield call(getStatusAmlSaga)
  }
}

function * watchProfiling () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_PROFILING)
    yield call(profilingSaga, payload)
  }
}

function * watchGetTabBar () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_GET_TAB_BAR)
    yield call(getTabBarSaga, payload)
  }
}

function * watchGetNavigationMenu () {
  while (yield take(userProfilingActions.actionTypes.DO_GET_NAVIGATION_MENU)) {
    yield call(getNavigationMenuSaga)
  }
}

function * watchGetNavigationProfile () {
  while (yield take(userProfilingActions.actionTypes.DO_GET_NAVIGATION_PROFILE)) {
    yield call(getNavigationProfileSaga)
  }
}

function * watchNewMessages () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_GET_NEW_MESSAGES)
    yield call(newMessagesSaga, payload)
  }
}
function * watchNewDocuments () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_GET_NEW_DOCUMENTS)
    yield call(newDocumentsSaga, payload)
  }
}

function * watchGetShopList () {
  yield takeLatest(shopPickerActions.actionTypes.DO_GET_SHOP_LIST, function * (action) {
    yield call(getShopList, action.payload)
  })
}

function * watchGetListOfShopsGt () {
  while (true) {
    const { pageNumber, pageSize } = yield take(shopPickerActions.actionTypes.DO_GET_LIST_OF_SHOPS_GT)
    yield call(getListOfShopsGt, { pageNumber, pageSize })
  }
}

function * watchSetShopSelection () {
  while (true) {
    const { group, data, selectorGroups } = yield take(shopPickerActions.actionTypes.DO_SET_SHOP_SELECTION)
    yield call(setShopSelection, group, data, selectorGroups)
  }
}

function * watchSetCostsShopSelection () {
  while (true) {
    const { payload } = yield take(shopPickerActions.actionTypes.DO_SET_COSTS_SHOP_SELECTION)
    yield call(setCostsShopSelection, payload)
  }
}

function * watchGetResultsGraphViewed () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_GET_RESULTS_GRAPH_VIEWED)
    yield call(getResultsGraphViewedSaga, payload)
  }
}

function * watchCostsGetShopList () {
  yield takeLatest(shopPickerActions.actionTypes.DO_GET_COSTS_SHOP_LIST, function * (action) {
    yield call(getCostsShopList, action.payload)
  })
}

function * watchGetDisputeShopList () {
  yield takeLatest(shopPickerActions.actionTypes.DO_GET_DISPUTE_SHOP_LIST, function * (action) {
    yield call(getDisputeShopList, action.payload)
  })
}

function * watchSetDisputeShopSelection () {
  while (true) {
    const { payload } = yield take(shopPickerActions.actionTypes.DO_SET_DISPUTE_SHOP_SELECTION)
    yield call(setDisputeShopSelection, payload)
  }
}

function * watchUpdateTutorial () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_UPDATE_TUTORIAL)
    yield call(updateTutorialSaga, payload)
  }
}

function * watchGetServiceQuickLinks () {
  while (true) {
    const { payload } = yield take(userProfilingActions.actionTypes.DO_GET_SERVICE_QUICK_LINKS)
    yield call(getServiceQuickLinksSaga, payload)
  }
}
/**
 * Root saga manages watcher lifecycle
 */
export default function * rootSaga () {
  yield all([
    watchGetTabBar(),
    watchProfiling(),
    watchGetNavigationProfile(),
    watchGetNavigationMenu(),
    // watchLocationChange(),
    watchNewMessages(),
    watchNewDocuments(),
    watchGetShopList(),
    watchSetShopSelection(),
    watchSetCostsShopSelection(),
    watchGetListOfShopsGt(),
    watchGetStatusAml(),
    watchGetResultsGraphViewed(),
    watchCostsGetShopList(),
    watchGetDisputeShopList(),
    watchSetDisputeShopSelection(),
    watchUpdateTutorial(),
    watchGetServiceQuickLinks()
    // @generator saga:fork
  ])
}
